<template>
  <div class="nx-seo-metas-field mt-4 mb-5">
    <div class="nx-seo-metas-field__header" :style="style">
      <h3 class="heading">{{ label }}</h3>
      <v-tooltip v-if="!disabled" bottom>
        <v-btn icon small @click="wizard = true" slot="activator">
          <v-icon>add</v-icon>
        </v-btn>
        <span>{{ $t("message.seo.metas.add") }}</span>
      </v-tooltip>
    </div>
    <v-data-table :headers="tableHeaders"
                  :pagination.sync="pagination"
                  :items="tableItems"
                  class="elevation-1">
      <template slot="items" slot-scope="props">
        <td class="text-xs-left"><code>{{ props.item.metaAsText }}</code></td>
        <td v-if="inTaxonomy" class="text-xs-left">
          <v-chip small v-if="props.item.meta.applyTo">
            {{ $t(`message.seo.metas.applyTo.${props.item.meta.applyTo}`) }}
          </v-chip>
          <v-chip small v-else>
            {{ $t('message.seo.metas.own') }}
          </v-chip>
        </td>
        <td class="text-xs-left">
          <nx-seo-origin-chip :value="props.item.meta.origin"></nx-seo-origin-chip>
        </td>
        <td v-if="!disabled" lass="text-xs-right px-0">
          <v-layout justify-end>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn class="mx-0 mr-1 px-0" icon @click="edit(props.item.meta)" v-on="on">
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('message.common.edit') }}</span>
            </v-tooltip>

            <v-btn class="mx-0 mr-1 px-0" icon v-if="!props.item.meta.origin" @click="remove(props.item.meta)">
              <v-icon>delete</v-icon>
            </v-btn>
          </v-layout>
        </td>
      </template>
    </v-data-table>

    <v-dialog v-model="wizard" width="600" lazy>
      <nx-seo-metas-wizard
        :value="selectedMeta"
        :with-apply-to="inTaxonomy"
        @input="onWizardInput"
        @cancel="onWizardCancel">
      </nx-seo-metas-wizard>
    </v-dialog>
  </div>
</template>

<script>
import OriginChip from '@/modules/seo/components/OriginChip';
import MetasWizard from '@/modules/seo/components/MetasWizard';
import BaseField from '@/modules/seo/components/BaseField';
import { objectHash } from '@/utils';
import { pickBy, pick } from 'lodash';

export default {
  name: 'nx-seo-metas-field',
  components: {
    [OriginChip.name]: OriginChip,
    [MetasWizard.name]: MetasWizard,
  },
  extends: BaseField,
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    inheritedMetas: {
      type: Array,
      default: () => [],
    },
    inTaxonomy: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
    },
  },
  data() {
    return {
      selectedMeta: null,
      wizard: false,
      pagination: {
        rowsPerPage: 10,
      },
    };
  },
  computed: {
    draft: {
      get() {
        return [...this.value, ...this.inheritedMetas];
      },
      set(value) {
        this.$emit('input', this.name, value);
      },
    },
    /**
     * Meta unique valid attributes
     * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/meta
     */
    uniqueAttributes() {
      return [
        'id',
        'name',
        'httpEquiv',
        'property',
        'charset',
      ];
    },
    /**
     * Meta allowed attributes
     * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/meta
     */
    allowedAttributes() {
      return [
        ...this.uniqueAttributes,
        'content',
        'lang',
      ];
    },
    /*
     * Own metas with unique identifier as uuid
     * @return []
     */
    ownMetasWithIds() {
      return this.value.map(meta => Object.assign({}, meta, { uuid: this.generateMetaId(meta) }));
    },
    /*
     * Inherited metas with unique identifier uuid
     * @return []
     */
    inheritedMetasWithIds() {
      return this.inheritedMetas.map(meta => Object.assign({}, meta, { uuid: this.generateMetaId(meta) }));
    },
    /*
     * Own and inherited metas merged and filtered based on the unique identifier uuid
     * @return []
     */
    filteredMetasWithIds() {
      return [
        ...this.ownMetasWithIds,
        ...this.inheritedMetasWithIds
          .filter(inheritedMeta => this.ownMetasWithIds.every(ownMeta => inheritedMeta.uuid !== ownMeta.uuid)),
      ];
    },
    /*
     * Formatted metas as table items
     * @return Array
     */
    tableItems() {
      return this.filteredMetasWithIds
        .map((meta) => {
          const allowedAttrs = pickBy(meta, (value, attr) => this.allowedAttributes.includes(attr));
          const formattedAttrs = Object
            .entries(allowedAttrs)
            .map(([attr, value]) => {
              if (value) {
                return `${attr}="${value}"`;
              }
              return null;
            })
            .filter(element => element !== null)
            .join(' ');
          return {
            metaAsText: `<meta ${formattedAttrs}>`,
            meta,
          };
        });
    },
    /*
     * Formatted table headers
     * @return Array
     */
    tableHeaders() {
      const headers = [
        {
          text: 'Meta',
          sortable: false,
          value: 'name',
        },
        {
          text: 'Aplica a',
          sortable: false,
          value: 'type',
        },
        {
          text: 'Origen',
          value: 'origin',
        },
        {
          text: null,
          sortable: false,
          value: 'actions',
        },
      ];
      if (!this.inTaxonomy) {
        return [...headers.slice(0, 1), ...headers.slice(2, 3)];
      }
      return headers;
    },
    style() {
      if (!this.label) {
        return { 'justify-content': 'flex-end' };
      }
      return null;
    },
  },
  methods: {
    generateMetaId(meta) {
      // Prepare the fields of the meta for generate the id
      // Use the id attribute if present
      // Use the main meta attributes "name", "httpEquiv", "property", "charset" and their value
      // Use the applyTo field if present
      return objectHash(pick(meta, [...this.uniqueAttributes, 'applyTo']));
    },
    remove(meta) {
      this.$emit('input', this.name, this.value.filter(item => this.generateMetaId(item) !== meta.uuid));
    },
    edit(meta) {
      this.selectedMeta = meta;
      this.wizard = true;
    },
    /*
     * Wizard methods
     */
    onWizardInput(metas) {
      this.$emit('input', this.name, [
        ...metas,
        ...this.value
          .filter(meta => metas.every(newMeta => this.generateMetaId(newMeta) !== this.generateMetaId(meta))),
      ]);
    },
    onWizardCancel() {
      this.selectedMeta = null;
      this.wizard = false;
    },
  },
};
</script>


<style lang="stylus" scoped>
@import '../../../assets/stylus/variables.styl'

.nx-seo-metas-field {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .heading {
    color: $nx-primary--darken-2;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    font-weight: 500;
  }
}
</style>
